/**
 * Phase 7 Commit J. Time step — hero "Next available" card (not for Be
 * You) + scrollable date strip + 3-col slot grid. Tap a slot to reserve (App fires
 * selectSlot which calls reserveSlot); user reviews held countdown
 * above and taps Continue in the RunningBar to advance to Contact.
 */

import { useMemo } from 'preact/hooks';
import type { AvailableSlot } from '../types';
import { ArrowIcon, GlobeIcon } from '../icons';

interface Props {
  availableDates: string[];
  availableSlots: AvailableSlot[];
  selectedDate: string | null;
  selectedSlot: AvailableSlot | null;
  timezone: string;
  loading: boolean;
  onSelectDate: (date: string) => void;
  onSelectSlot: (slot: AvailableSlot) => void;
  /**
   * Per-tenant view-bundle flag from bootstrap.branding.customViewBundle.
   * Drives copy variants in this step. Null = the default copy ("Pick a
   * time" eyebrow + "Your stylist's free, soon." headline). 'be-you' =
   * the med-spa CRO copy (no eyebrow, suite/treatment language).
   */
  customViewBundle?: string | null;
  /**
   * True when the booked service is a consultation — Be You's picker flag or
   * any tenant's service whose name matches /consult/i (Odessa). Swaps the
   * treatment/stylist wording on this step for consultation copy.
   */
  isConsult?: boolean;
}

function formatDow(date: string): string {
  return new Date(date + 'T00:00:00Z').toLocaleDateString('en-US', {
    weekday: 'short',
    timeZone: 'UTC',
  });
}

function formatDay(date: string): string {
  return String(new Date(date + 'T00:00:00Z').getUTCDate());
}

function formatMonth(date: string): string {
  return new Date(date + 'T00:00:00Z').toLocaleDateString('en-US', {
    month: 'short',
    timeZone: 'UTC',
  });
}

function formatTime(iso: string, tz: string): string {
  return new Date(iso).toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    timeZone: tz,
  });
}

function formatHeroWhen(iso: string, tz: string): string {
  return new Date(iso).toLocaleString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    timeZone: tz,
  });
}

export function TimeStep({
  availableDates,
  availableSlots,
  selectedDate,
  selectedSlot,
  timezone,
  loading,
  onSelectDate,
  onSelectSlot,
  customViewBundle,
  isConsult,
}: Props) {
  const isBeYou = customViewBundle === 'be-you';
  const heroSlot = availableSlots[0];
  const isToday = (date: string) => {
    const today = new Date();
    return (
      date ===
      `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`
    );
  };

  // Scarcity heuristic: when fewer than 4 evening slots remain on the
  // first available day, mark the hero card with a "last evening" pill.
  // Hour parsed in the salon's timezone (the slot's local time, not the
  // viewer's) so a customer in EST viewing a PST salon doesn't see a
  // 5pm-PST slot bucket as 8pm-EST evening.
  // H4 — share one Intl.DateTimeFormat across the slot loop (formatter
  // construction is the bulk of the cost) and only recompute the count
  // when the slot list / tz actually changes.
  const eveningSlotsCount = useMemo(() => {
    if (!heroSlot) return 0;
    const fmt = new Intl.DateTimeFormat('en-US', {
      hour: 'numeric',
      hour12: false,
      timeZone: timezone,
    });
    return availableSlots.filter((s) => {
      const h = parseInt(fmt.format(new Date(s.startAt)), 10);
      return h >= 17;
    }).length;
  }, [heroSlot, availableSlots, timezone]);
  const showScarcity =
    !!heroSlot && eveningSlotsCount > 0 && eveningSlotsCount <= 4;

  return (
    <div class="sbw-scroll">
      {!isBeYou && <div class="sbw-eyebrow">Pick a time</div>}
      <h2 class="sbw-step-title">
        {isBeYou
          ? isConsult
            ? heroSlot
              ? 'Reserve your consultation.'
              : 'Choose your consultation time.'
            : heroSlot
              ? 'Reserve your treatment.'
              : 'Choose your appointment.'
          : heroSlot
            ? isConsult
              ? 'Reserve your consultation.'
              : "Your stylist's free, soon."
            : isConsult
              ? 'Pick a time for your consultation.'
              : 'Pick a date and time.'}
      </h2>
      {heroSlot && (
        <p class="sbw-step-sub">
          {isBeYou
            ? isConsult
              ? 'Pick a time below. We’ll hold your consultation slot for 8 minutes.'
              : 'Pick a time below. These book up fast — we’ll hold it for 8 minutes.'
            : isConsult
              ? 'The soonest consultation opening is below — most take about 30 minutes.'
              : 'The next slot is the closest match for your service.'}
        </p>
      )}

      {/* Be You asked for this card to go (2026-10-12 call): next to the
          slot grid, a second way to pick read as an extra option clients
          had to work out for themselves. */}
      {selectedDate && heroSlot && !isBeYou && (
        <div class="sbw-next-card">
          <div class="sbw-next-eyebrow">
            <span class="sbw-dot" /> Next available
            {showScarcity && (
              <span class="sbw-next-scarcity">
                {eveningSlotsCount === 1
                  ? 'Last evening slot'
                  : `${eveningSlotsCount} evening slots left`}
              </span>
            )}
          </div>
          <div class="sbw-next-time">
            {formatTime(heroSlot.startAt, timezone)}
          </div>
          <div class="sbw-next-when">
            {formatHeroWhen(heroSlot.startAt, timezone)}
            {heroSlot.staffName ? ` · with ${heroSlot.staffName}` : ''}
          </div>
          {/* The slot-grid buttons below already gate on `loading`; this hero
              CTA did not, so tapping it mid-reserve fired a second reserveSlot
              against the same cart. */}
          <button
            type="button"
            class="sbw-next-cta"
            disabled={loading}
            onClick={() => onSelectSlot(heroSlot)}
          >
            Lock it in <ArrowIcon />
          </button>
        </div>
      )}

      <div
        class="sbw-date-strip"
        role="radiogroup"
        aria-label="Pick a date"
      >
        {availableDates.map((d) => {
          const isActive = selectedDate === d;
          const today = isToday(d);
          return (
            <div
              key={d}
              class={
                'sbw-date' +
                (isActive ? ' is-active' : '') +
                (today ? ' is-today' : '')
              }
              onClick={() => onSelectDate(d)}
              role="radio"
              aria-checked={isActive}
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onSelectDate(d);
                }
              }}
            >
              <span class="sbw-date-dow">{formatDow(d)}</span>
              <span class="sbw-date-day">{formatDay(d)}</span>
              <span class="sbw-date-mo">{formatMonth(d)}</span>
            </div>
          );
        })}
      </div>

      {selectedDate ? (
        availableSlots.length > 0 ? (
          // Keep the grid mounted whenever we have slots — even if a
          // reserveSlot is in flight from the user tapping one. The
          // selected pill is highlighted via `is-selected` and the
          // running bar conveys the loading state; swapping the entire
          // grid out for "Loading times…" mid-tap reads as a flicker.
          <div class="sbw-slots">
            {availableSlots.map((slot) => {
              const isSelected = selectedSlot?.slotId === slot.slotId;
              return (
                <button
                  key={slot.slotId}
                  type="button"
                  class={'sbw-slot' + (isSelected ? ' is-selected' : '')}
                  onClick={() => onSelectSlot(slot)}
                  disabled={loading}
                >
                  {formatTime(slot.startAt, timezone)}
                </button>
              );
            })}
          </div>
        ) : loading ? (
          <div class="sbw-loading">Loading times…</div>
        ) : (
          <div class="sbw-empty">No times available for this day.</div>
        )
      ) : loading ? (
        // Date strip still empty, dates fetch in flight. Common on PHD
        // where the availableDates call fires AFTER Contact submit and
        // can take several seconds against GHL — the old static
        // "Select a date above" copy made the widget look frozen.
        <div class="sbw-loading">Loading available dates…</div>
      ) : availableDates.length === 0 ? (
        <div class="sbw-empty">No dates available right now.</div>
      ) : (
        <div class="sbw-hint">Select a date above to see available times.</div>
      )}

      <div class="sbw-tz">
        <GlobeIcon /> All times shown in the salon&apos;s local time
      </div>
    </div>
  );
}
